/**
 * Change history section rendered at the bottom of a corrected note.
 * Each addendum is shown in its own card with metadata and tracked changes.
 */

import { Card, Divider, Group, Stack, Text } from '@mantine/core';
import { formatShortDateTime } from '../utils/dateFormatters';
import type { Addendum } from './types';
import { ADDENDUM_REASON_OPTIONS } from './types';
import { AddendumTrackedChangesView } from '@/components/AddendumTrackedChangesView';

function getReasonLabel(code: Addendum['reasonForChange']): string {
  return ADDENDUM_REASON_OPTIONS.find((o) => o.value === code)?.label ?? code;
}

export interface CorrectedNoteChangeHistoryProps {
  originalNoteDate: string;
  addendums: Addendum[];
}

export function CorrectedNoteChangeHistory({
  addendums,
}: CorrectedNoteChangeHistoryProps): JSX.Element {
  if (addendums.length === 0) return <></>;

  return (
    <Stack gap="sm" mt="md">
      <Divider />
      <Text size="xs" c="gray.6" fw={600} tt="uppercase">
        Change history
      </Text>

      {addendums.map((addendum) => {
        const authorLine = addendum.authorCredentials
          ? `${addendum.authorName}, ${addendum.authorCredentials}`
          : addendum.authorName;
        const reasonLabel =
          'reasonForChange' in addendum && addendum.reasonForChange
            ? getReasonLabel(addendum.reasonForChange)
            : '—';
        return (
          <Card key={addendum.id} withBorder radius="md">
            <Stack gap={6}>
              <Group justify="space-between" wrap="wrap">
                <Text size="sm" fw={600} c="gray.8">
                  {addendum.label}
                </Text>
                <Text size="xs" c="gray.5">
                  {formatShortDateTime(addendum.timestamp)}
                </Text>
              </Group>

              <Group gap="lg" wrap="wrap">
                {/* Full datetime so reviewers can see exactly when the original note was created/signed */}
                <Text size="xs" c="gray.6">
                  Original note signed: {formatShortDateTime(addendum.originalNoteDate)}
                </Text>
                <Text size="xs" c="gray.6">
                  Author: {authorLine}
                </Text>
                <Text size="xs" c="gray.6">
                  Reason: {reasonLabel}
                </Text>
              </Group>

              <Divider />

              <AddendumTrackedChangesView
                lineChanges={addendum.lineChanges}
                comments={addendum.lineChanges?.length ? addendum.body : undefined}
                fallbackBody={addendum.lineChanges?.length ? undefined : addendum.body}
                originalLines={addendum.originalLines}
                afterLines={addendum.afterLines}
              />
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );
}
